// Trigger frontend redeploy
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import { isMsalConfigured } from "../auth/msalConfig";
import useUserContext from "../auth/useUserContext";
import { fetchEntitlements } from "../services/entitlements";
import { fetchMe } from "../utils/api";
import SecureShield365, { pageFromHash } from "./SecureShield365";
import ClientPicker from "./ClientPicker";
import ServiceInquiryModal from "./ServiceInquiryModal";
import IdleWarningModal from "./IdleWarningModal";
import useIdleTimeout from "../hooks/useIdleTimeout";

/* ═══════════════════════════════════════════════════════════════
   PortalShell — Top-level wrapper with an inline service tile
   bar embedded in the header. Tiles switch the dashboard below.
   ═══════════════════════════════════════════════════════════════ */

const T = {
  bg: "#0b1f3a",
  panel: "#112b4d",
  panel2: "#15365f",
  border: "rgba(255,255,255,0.08)",
  text: "#edf4ff",
  muted: "#a8bddc",
  blue: "#44a6ff",
  green: "#53c46d",
  yellow: "#f3b53f",
  orange: "#ff7a45",
  red: "#e14d57",
};

/* ── SecureShield tier ordering for dynamic tag labels ── */
const TIER_ORDER = { "Bronze": 0, "Silver": 1, "Gold": 2, "Platinum": 3 };

/* The SecureShield dashboard slugs (any tier). Used to gate both selection
   (handleSelect) and the main-panel render (showSecureShield). */
const SS_SLUGS = ["secureshield", "secureshield-platinum", "secureshield-silver"];
const isSSSlug = (slug) => SS_SLUGS.includes(slug);
const SLUG_TO_TIER = {
  "secureshield-silver": "Silver",
  "secureshield": "Gold",
  "secureshield-platinum": "Platinum",
};
// Inverse of SLUG_TO_TIER: a SecureShield tier name → its tier-specific slug.
// The entitlements API only ever emits the raw "secureshield" slug (tier is a
// separate field), so this maps the customer's actual tier to the slug used for
// both selection (the pill ring) and the resolved active state.
const TIER_TO_SLUG = { Silver: "secureshield-silver", Gold: "secureshield", Platinum: "secureshield-platinum" };

/**
 * Returns { tag, tagType } for a SecureShield tile based on the customer's
 * actual tier from the API. Vocabulary is Active / Upgrade only:
 *   - current tier (same rank)      → Active  (green)
 *   - ANY higher tier (above rank)  → Upgrade (blue)
 *   - any lower tier (below rank)   → null sentinel; the caller
 *     (visibleServices) filters these out so they never render (no downgrades).
 */
function ssTag(tileSlug, customerTier) {
  const tileTier = SLUG_TO_TIER[tileSlug];
  if (!tileTier || !customerTier) return null;
  const tileRank = TIER_ORDER[tileTier] ?? -1;
  const custRank = TIER_ORDER[customerTier] ?? -1;
  if (custRank < 0) return null;
  if (tileRank === custRank) return { tag: "Active", tagType: "green" };
  if (tileRank > custRank) return { tag: "Upgrade", tagType: "blue" };
  return null;
}

/* ── Service definitions ── */
const SERVICES = [
  {
    slug: "secureshield-silver",
    title: "SecureShield 365 Silver",
    shortTitle: "Silver",
    tier: "Silver",
    tag: null, // dynamic — set by ssTag()
    tagType: null,
    pageTitle: "Security Posture Dashboard",
    pageSubtitle: "SecureShield 365 Silver \u2014 Baseline Assessment",
    description: "Baseline Microsoft 365 security assessment with remediation and a monthly report of significant changes.",
    features: ["Assessment + remediation", "Monthly change report"],
    cta: null,
    learnMoreUrl: "https://www.patriotconsulting.com/services/secureshield365",
    inquiryService: "SecureShield365 Managed Services",
  },
  {
    slug: "secureshield",
    title: "SecureShield 365 Gold",
    shortTitle: "Gold",
    tier: "Gold",
    tag: null,
    tagType: null,
    pageTitle: "Security Posture Dashboard",
    pageSubtitle: "SecureShield 365 \u2014 Quarterly Assessment",
    description: "Comprehensive M365 security assessment with remediation, Azure cloud posture management, quarterly delta audits, compliance framework cross-mapping, annual incident response, and unlimited Level 3 support.",
    features: ["Quarterly delta audits", "Azure CSPM", "Compliance mapping", "Unlimited L3 support"],
    cta: "View your dashboard",
    learnMoreUrl: "https://www.patriotconsulting.com/services/secureshield365",
    inquiryService: "SecureShield365 Managed Services",
  },
  {
    slug: "secureshield-platinum",
    title: "SecureShield 365 Platinum",
    shortTitle: "Platinum",
    tier: "Platinum",
    tag: null,
    tagType: null,
    pageTitle: "Security Posture Dashboard",
    pageSubtitle: "SecureShield 365 Platinum \u2014 Continuous Monitoring",
    description: "Everything in Gold plus full Microsoft Intune deployment across Windows, macOS, iOS, and Android, quarterly penetration testing, and a second annual M365 incident response.",
    features: ["Full Intune deployment", "All platforms", "Quarterly penetration testing", "2x incident response"],
    cta: "Learn more",
    learnMoreUrl: "https://www.patriotconsulting.com/services/secureshield365",
    inquiryService: "SecureShield365 Managed Services",
  },
  {
    slug: "adv-deploy",
    title: "Security Deployments",
    shortTitle: "Deployments",
    tag: null, // dynamic \u2014 set by entitlement active state in visibleServices
    tagType: null,
    pageTitle: "Security Deployment",
    pageSubtitle: "Security Deployments \u2014 Scoped Engagement",
    description: "Project-based deployment of Microsoft security controls \u2014 Defender XDR, Conditional Access, Intune compliance, Purview, and Sentinel.",
    features: ["Scoping call", "Runbook", "Knowledge transfer"],
    cta: "Request a scoping call",
    learnMoreUrl: "https://www.patriotconsulting.com/services/security-deployments",
    inquiryService: "Security, Compliance & Identity Deployments",
  },
  {
    slug: "mxdr",
    title: "MXDR365",
    shortTitle: "MXDR365",
    tag: null, // dynamic \u2014 set by entitlement active state in visibleServices
    tagType: null,
    pageTitle: "Threat Detection & Response",
    pageSubtitle: "MXDR365 \u2014 Managed Extended Detection & Response",
    description: "Full Sentinel SIEM and M365 Defender deployment with 24x7x365 expert SOC analysis, SOAR automation, threat intelligence, and threat hunting.",
    features: ["24x7x365 SOC", "SOAR automation", "Threat hunting"],
    cta: "See what\u2019s included",
    learnMoreUrl: "https://www.patriotconsulting.com/services/mxdr365",
    inquiryService: "MXDR365 - Managed Detection & Response",
  },
  {
    slug: "academy",
    title: "Patriot Academy",
    shortTitle: "Academy",
    tag: "Active",
    tagType: "green",
    pageTitle: "Training Library",
    pageSubtitle: "Patriot Academy \u2014 On-Demand Training",
    description: "On-demand admin training covering Microsoft 365 security, compliance, and identity management.",
    features: ["Video library", "Hands-on labs", "Cert prep"],
    cta: "View training library",
    learnMoreUrl: "https://premium.patriotconsulting.com/",
    inquiryService: "Patriot Academy",
  },
  {
    slug: "csp",
    title: "Cloud Service Provider",
    shortTitle: "CSP",
    tag: null, // dynamic — set by entitlement active state in visibleServices
    tagType: null,
    pageTitle: "Cloud Service Provider",
    pageSubtitle: "Microsoft Licensing & Subscription Management",
    description: "Microsoft licensing through Patriot as your Cloud Solution Provider — consolidated billing, license optimization, and Patriot-only SecureCloud365 bundle pricing.",
    features: ["License optimization", "Consolidated billing", "Bundle offers"],
    cta: "Talk to your AE",
    learnMoreUrl: "https://www.patriotconsulting.com/services/csp",
    inquiryService: "Cloud Solution Provider",
  },
];

const TAG_COLORS = {
  green:  { bg: "rgba(83,196,109,0.2)",  c: "#d9ffe2" },
  blue:   { bg: "rgba(68,166,255,0.2)",  c: "#d8ecff" },
  orange: { bg: "rgba(255,122,69,0.2)",  c: "#ffe0d4" },
  purple: { bg: "rgba(92,45,145,0.25)",  c: "#e8d5ff" },
  red:    { bg: "rgba(225,77,87,0.2)",   c: "#ffd8db" },
  muted:  { bg: "rgba(255,255,255,0.06)", c: T.muted },
};

// Solid dot colours for the compact-header status dot, keyed by the same
// tagType vocabulary the word badges use (TAG_COLORS): green active, blue
// upgrade, neutral inactive. No new colours — these are the theme solids the
// active dot already uses.
const DOT_COLORS = { green: T.green, blue: T.blue, muted: T.muted };

/* ── Compact header breakpoint ──
   Below this viewport width the pill's status badge collapses to a coloured
   dot so the SERVICE NAME keeps the space (the name is the information; the
   badge is decoration). Measured: the full header (internal chrome, 7 pills,
   word badges at their natural widths) needs ~1541px with system fonts; DM
   Sans metrics vary a few percent, so 1600 leaves margin. Engaging the dot
   a little early is benign — status stays visible as colour + tooltip. */
const COMPACT_HEADER_QUERY = "(max-width: 1599px)";
function useCompactHeader() {
  const [compact, setCompact] = useState(() =>
    typeof window.matchMedia === "function"
      ? window.matchMedia(COMPACT_HEADER_QUERY).matches
      : false
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(COMPACT_HEADER_QUERY);
    const onChange = (e) => setCompact(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return compact;
}

/* Pill width floors, coupled to the nav's derived minWidth below.
   - Word-badge mode (wide): 100px — badge intact, label ellipsises.
   - Compact/dot mode: 64px — the ~50px badge is gone, so the floor can drop;
     64 keeps a colour dot + ~3-4 label chars at absolute worst squeeze.
   Longest measured natural dot-mode pill is "Deployments" at ~117px, so a
   100px floor would NOT fit every full name — the lower floor plus natural
   sizing (pills only shrink when space actually runs out) is what restores
   full names at laptop widths. */
const PILL_MIN_COMPACT = 64;
const PILL_MIN_FULL = 100;

const DEFAULT_ACTIVE_SLUGS = ["secureshield", "academy"];

/* ── Resolve default selection ── */
function getDefaultSlug(activeSlugs) {
  const ssOrder = ["secureshield", "secureshield-platinum", "secureshield-silver"];
  for (const s of ssOrder) {
    if (activeSlugs.includes(s)) return s;
  }
  return activeSlugs[0] || SERVICES[0].slug;
}


/* ═══════════════════════════════════════════════════
   ServiceTile — Compact header tile with hover popover
   ═══════════════════════════════════════════════════ */
const ServiceTile = ({ service, isActive, isSelected, onSelect, onRequestInfo, compact = false }) => {
  const [hovered, setHovered] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const tagStyle = TAG_COLORS[service.tagType] || TAG_COLORS.muted;
  const tileRef = useRef(null);
  const hoverTimeout = useRef(null);

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (tileRef.current) {
      const rect = tileRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setHovered(true);
  };
  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setHovered(false), 150);
  };

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  // Close popover on scroll — viewport coords would otherwise go stale.
  useEffect(() => {
    if (!hovered) return;
    const close = () => setHovered(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [hovered]);

  return (
    <div
      ref={tileRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={() => onSelect(service.slug)}
      style={{
        position: "relative",
        // Shrinkable to a floor (see PILL_MIN_*). The explicit minWidth
        // matters: with min-width:auto the flex floor is the LABEL TEXT's
        // intrinsic width (~120-150px/tile), which pinned the bar at ~976px
        // for 7 tiles and horizontally overflowed the whole document on
        // laptop widths.
        minWidth: compact ? PILL_MIN_COMPACT : PILL_MIN_FULL,
        cursor: "pointer",
      }}
    >
      {/* ── Compact tile ── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "7px 12px",
        borderRadius: 10,
        border: `1px solid ${
          isSelected
            ? "rgba(68,166,255,0.5)"
            : isActive
            ? "rgba(83,196,109,0.25)"
            : T.border
        }`,
        background: isSelected
          ? "rgba(68,166,255,0.12)"
          : "rgba(255,255,255,0.03)",
        boxShadow: isSelected
          ? "0 0 12px rgba(68,166,255,0.15)"
          : "none",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        minWidth: compact ? PILL_MIN_COMPACT : PILL_MIN_FULL,
        maxWidth: 150,
      }}>
        {/* Status dot. Wide mode: green dot on active tiles only (the word
            badge carries the status). Compact mode: the dot IS the status —
            coloured by the badge's tagType vocabulary — so it renders on
            every tile and carries an accessible label since the word is
            gone. One dot either way, never dot + dot. */}
        {(compact || isActive) && (
          <span
            role={compact ? "img" : undefined}
            aria-label={compact ? service.tag || undefined : undefined}
            title={compact ? service.tag || undefined : undefined}
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: compact
                ? (DOT_COLORS[service.tagType] || DOT_COLORS.muted)
                : T.green,
              boxShadow: isSelected && isActive ? `0 0 6px ${T.green}` : "none",
              flexShrink: 0,
            }} />
        )}
        {/* Name */}
        <span style={{
          fontSize: 12,
          fontWeight: isSelected ? 700 : 600,
          color: isSelected ? "#fff" : isActive ? T.text : T.muted,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {service.shortTitle}
          {service.slug === "academy" && (
            <span style={{ marginLeft: 4, opacity: 0.8 }}>{"\u2197"}</span>
          )}
        </span>
        {/* Tag — word badge in wide mode only; compact mode uses the dot. */}
        {!compact && (
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: 999,
            background: tagStyle.bg,
            color: tagStyle.c,
            flexShrink: 0,
            lineHeight: 1.3,
          }}>
            {service.tag}
          </span>
        )}
      </div>

      {/* ── Hover popover (portal'd to document.body to escape header stacking context) ── */}
      {hovered && createPortal(
        <div
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          style={{
            position: "fixed",
            top: popoverPos.top,
            left: popoverPos.left,
            transform: "translateX(-50%)",
            width: 280,
            background: `linear-gradient(180deg, ${T.panel}, ${T.panel2})`,
            border: `1px solid ${isActive ? "rgba(83,196,109,0.25)" : T.border}`,
            borderRadius: 14,
            padding: 16,
            boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
            zIndex: 1100,
            animation: "fadeIn 0.15s ease",
          }}
        >
          {/* Arrow */}
          <div style={{
            position: "absolute",
            top: -6,
            left: "50%",
            transform: "translateX(-50%) rotate(45deg)",
            width: 12,
            height: 12,
            background: T.panel,
            borderLeft: `1px solid ${isActive ? "rgba(83,196,109,0.25)" : T.border}`,
            borderTop: `1px solid ${isActive ? "rgba(83,196,109,0.25)" : T.border}`,
          }} />

          {/* Title row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {isActive && (
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                background: T.green, boxShadow: `0 0 6px ${T.green}`, flexShrink: 0,
              }} />
            )}
            <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
              {service.title}
            </span>
            {/* Compact mode drops the word badge from the pill, so the
                popover carries it — full name AND status on hover. */}
            {compact && service.tag && (
              <span style={{
                fontSize: 9,
                fontWeight: 700,
                padding: "2px 6px",
                borderRadius: 999,
                background: tagStyle.bg,
                color: tagStyle.c,
                flexShrink: 0,
                lineHeight: 1.3,
              }}>
                {service.tag}
              </span>
            )}
          </div>

          {/* Description */}
          <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, margin: "0 0 10px" }}>
            {service.description}
          </p>

          {/* Feature tags */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
            {service.features.map(f => (
              <span key={f} style={{
                fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 6,
                background: "rgba(255,255,255,0.05)", border: `1px solid ${T.border}`, color: T.muted,
              }}>{f}</span>
            ))}
          </div>

          {/* CTA — Active tiles get NO button (already purchased, and "Open
              dashboard" dead-ends for everything but SecureShield; the tile
              itself still handles clicks — SS navigates, Academy opens
              externally). Inactive/Upgrade tiles keep the split footer. */}
          {!isActive && (
            // Inactive / Upgrade tiles: split footer — ghost "Learn more" +
            // filled "Request info". Each is a single click; stopPropagation
            // keeps the tile's onSelect from firing underneath.
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (service.learnMoreUrl) {
                    window.open(service.learnMoreUrl, "_blank", "noopener,noreferrer");
                  }
                }}
                style={{
                  flex: 1, minWidth: 0, padding: "8px 12px", borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: "rgba(255,255,255,0.04)",
                  color: T.muted, cursor: "pointer", fontFamily: "inherit",
                  fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                  transition: "background 0.15s",
                }}
              >
                Learn more {"↗"}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onRequestInfo?.(service); }}
                style={{
                  flex: 1, minWidth: 0, padding: "8px 12px", borderRadius: 8,
                  border: "1px solid rgba(68,166,255,0.4)",
                  background: "rgba(68,166,255,0.15)",
                  color: T.blue, cursor: "pointer", fontFamily: "inherit",
                  fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                  transition: "background 0.15s",
                }}
              >
                Request info
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   Placeholder dashboard for services without a real UI
   ═══════════════════════════════════════════════════ */
const PlaceholderDashboard = ({ service, isActive }) => (
  <div style={{
    fontFamily: "'DM Sans',system-ui,sans-serif",
    color: T.text,
    minHeight: "calc(100vh - 52px)",
    background: "linear-gradient(180deg,#08182e,#0b1f3a)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}>
    <div style={{ textAlign: "center", maxWidth: 480, padding: "0 24px" }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: "0 auto 20px",
        background: isActive ? "rgba(83,196,109,0.1)" : "rgba(68,166,255,0.1)",
        border: `1px solid ${isActive ? "rgba(83,196,109,0.2)" : "rgba(68,166,255,0.2)"}`,
        display: "grid", placeItems: "center",
        fontSize: 28,
      }}>
        {service.slug === "academy" ? "\uD83C\uDF93" :
         service.slug === "mxdr" ? "\uD83D\uDEE1\uFE0F" :
         service.slug === "adv-deploy" ? "\uD83D\uDD27" :
         service.slug === "csp" ? "\u2601\uFE0F" : "\uD83D\uDD12"}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 6px", color: T.text }}>
        {service.pageTitle}
      </h2>
      <p style={{ fontSize: 14, color: T.muted, margin: "0 0 6px" }}>
        {service.title}
      </p>
      <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, margin: "0 0 24px", opacity: 0.7 }}>
        {isActive
          ? "This dashboard is coming soon. Check back for updates."
          : service.description}
      </p>
      {!isActive && service.cta && (
        <button style={{
          padding: "10px 24px", borderRadius: 10,
          border: `1px solid ${T.border}`,
          background: "rgba(255,255,255,0.04)",
          color: T.muted, cursor: "default", fontFamily: "inherit",
          fontSize: 13, fontWeight: 600,
        }}>
          {service.cta}
        </button>
      )}
    </div>
  </div>
);


/* ═══════════════════════════════════════════════════
   DashboardLoading — Neutral full-height loading state
   shown while a resolved customer's entitlements are
   still in flight. NOT an error and NOT a service
   placeholder — it must not assert any service state.
   ═══════════════════════════════════════════════════ */
const DashboardLoading = () => (
  <div style={{
    fontFamily: "'DM Sans',system-ui,sans-serif",
    color: T.muted,
    minHeight: "calc(100vh - 52px)",
    background: "linear-gradient(180deg,#08182e,#0b1f3a)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600 }}>
      <span style={{
        width: 16, height: 16, borderRadius: "50%",
        border: "2px solid rgba(168,189,220,0.25)",
        borderTopColor: T.blue,
        display: "inline-block",
        animation: "ssp-spin 0.7s linear infinite",
      }} />
      Loading your dashboard…
    </div>
  </div>
);


/* ═══════════════════════════════════════════════════
   SignOutButton — Subtle sign-out in the header
   ═══════════════════════════════════════════════════ */
const SignOutButton = () => {
  const { instance } = useMsal();
  return (
    <button
      onClick={() => instance.logoutRedirect()}
      title="Sign out"
      style={{
        padding: "4px 10px",
        borderRadius: 6,
        border: `1px solid ${T.border}`,
        background: "transparent",
        color: T.muted,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 11,
        fontWeight: 600,
        transition: "color 0.15s, border-color 0.15s",
      }}
    >
      Sign out
    </button>
  );
};


/* ═══════════════════════════════════════════════════
   PortalShell — Main export
   ═══════════════════════════════════════════════════ */
export default function PortalShell() {
  const { user } = useUserContext();
  // isInternal drives the idle limit: Patriot internal users get the 8h ceiling,
  // customers keep 30 min. Derived per render from the user object — never stored.
  const isInternal = user?.isInternal || false;
  const { showWarning, remainingMs, staySignedIn, signOutNow } = useIdleTimeout(isInternal);
  // Below the breakpoint the pill badges collapse to status dots and the
  // pill/nav floors drop — see COMPACT_HEADER_QUERY.
  const compactHeader = useCompactHeader();
  const [activeSlugs, setActiveSlugs] = useState(DEFAULT_ACTIVE_SLUGS);
  const [customerName, setCustomerName] = useState(null);
  const [ssTier, setSsTier] = useState(null); // SecureShield tier from API
  const [clientSlug, setClientSlug] = useState(null); // customer blob slug for /api/report/{slug}
  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
  // True when the entitlements fetch FAILED (as opposed to still in flight).
  // Distinguishes "loading" from "errored" so the main panel doesn't spin
  // forever on an entitlements failure (entitlements never auto-retries).
  const [entitlementsError, setEntitlementsError] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(() => getDefaultSlug(DEFAULT_ACTIVE_SLUGS));
  const [secureShieldPage, setSecureShieldPage] = useState(() => pageFromHash());
  const entitlementsFetched = useRef(false);

  // The customer selection the user has ACTED on this session:
  //   undefined — untouched: ONLY in this state may the frozen page-load
  //               ?client= snapshot (initialClientId below) drive the
  //               picker's restore, on the very first mount.
  //   null      — explicitly cleared via the picker's X: the frozen
  //               snapshot must NEVER resurface after any user action.
  //   string    — the customer id currently selected.
  // Fixes the presentation-mode revert: ClientPicker unmounts in
  // delivery mode and its remount re-runs the once-per-mount URL
  // restore, which used to read the stale page-load snapshot and
  // silently re-select the page-load customer (or, via the clear path,
  // resurrect a cleared one).
  const [sessionSelectionId, setSessionSelectionId] = useState(undefined);
  // Monotonic guard for the per-selection entitlements fetch: a slow
  // response for a customer no longer being viewed must never repaint
  // the services bar (classic stale-response race). Bumped on every
  // selection AND on clear.
  const entitlementsSeq = useRef(0);

  // ── Service inquiry ("Request info") modal ──
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryService, setInquiryService] = useState("");
  const onRequestInfo = useCallback((service) => {
    setInquiryService(service?.inquiryService || "");
    setInquiryOpen(true);
  }, []);

  // ── Delivery (session) mode ──
  // When ON, internal-only chrome is hidden so the portal is safe to screen-share
  // with a client: the ClientPicker (full client list), the Admin nav, and the
  // held-scan review banner. Persisted in sessionStorage so a mid-call refresh
  // doesn't drop out of delivery mode. Entering is one click (instant protection);
  // exiting requires a confirm so an accidental click can't expose internals.
  const [deliveryMode, setDeliveryMode] = useState(
    () => sessionStorage.getItem("deliveryMode") === "1"
  );
  useEffect(() => {
    if (deliveryMode) sessionStorage.setItem("deliveryMode", "1");
    else sessionStorage.removeItem("deliveryMode");
  }, [deliveryMode]);
  const toggleDeliveryMode = useCallback(() => {
    setDeliveryMode((on) => {
      if (on) {
        // Exiting — confirm so a stray click can't reveal internals mid-call.
        return !window.confirm("Exit Presentation Mode? Internal controls will become visible.");
      }
      // Entering — instant, no confirm.
      return true;
    });
  }, []);

  // Read the ?client={customer_id} param ONCE on mount. Passed to
  // ClientPicker as initialClientId so it can restore that customer after a
  // page refresh (the picker does the actual restore once its list loads).
  const [initialClientId] = useState(
    () => new URLSearchParams(window.location.search).get("client")
  );

  /* ── Write (or clear) the ?client={customer_id} URL param ──
     replaceState (not pushState) so switching customers doesn't pile up
     back-button history entries. Other query params + the hash are preserved.
     Pass null/empty to remove the param entirely. */
  const writeClientParam = useCallback((customerId) => {
    const params = new URLSearchParams(window.location.search);
    if (customerId) {
      params.set("client", customerId);
    } else {
      params.delete("client");
    }
    const qs = params.toString();
    const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, []);

  /* ── Apply entitlements data to local state ── */
  const applyEntitlements = useCallback((data) => {
    // SecureShield tier lives inside the secureshield service object (the API
    // emits the raw "secureshield" slug + a separate tier field), not at the top
    // level. Derive it locally here so the selection below can use it — the
    // ssTier STATE set further down is not yet updated within this same call.
    const ssService = data?.active_services?.find(
      (s) => (typeof s === "string" ? s : s.slug) === "secureshield"
    );
    const tier =
      ssService && typeof ssService === "object" ? ssService.tier : null;

    if (data?.active_services?.length) {
      // Handle both formats: array of strings ["secureshield"] or
      // array of objects [{ slug: "secureshield" }]
      const slugs = data.active_services.map((s) =>
        typeof s === "string" ? s : s.slug
      );
      setActiveSlugs(slugs);
      // applyEntitlements only ever runs on a FRESH customer load (the internal
      // picker fires it per selection; the customer effect is one-shot via the
      // entitlementsFetched ref) — never as an in-place re-fetch of the customer
      // currently being viewed. So we resolve the default selection for the new
      // customer rather than preserving the prior slug. A user's explicit pill
      // click (handleSelect) is never followed by another applyEntitlements for
      // the same customer, so this can't yank their selection.
      //
      // When the customer HAS a SecureShield tier, select that tier's specific
      // slug (TIER_TO_SLUG[tier], e.g. Silver → "secureshield-silver") so the
      // selected ring lands on their actual tier — matching the green active
      // state, which already resolves via TIER_TO_SLUG. The API only ever emits
      // the raw "secureshield" slug, so getDefaultSlug(slugs) alone would always
      // pick Gold. Non-SS customers (no tier) keep the getDefaultSlug fallback,
      // which preserves the non-SS → SS switch behavior.
      const tierSlug = tier ? TIER_TO_SLUG[tier] : null;
      setSelectedSlug(tierSlug || getDefaultSlug(slugs));
    }
    if (data?.customer_name) {
      setCustomerName(data.customer_name);
    }
    // NOTE: clientSlug is intentionally NOT set here. The customer's blob slug
    // for /api/report/{slug} is the authoritative resolved value from /api/me,
    // set in the identity effect below. Entitlements only drives the services
    // bar (active slugs + tier); it must never overwrite the resolved slug.
    if (tier) {
      setSsTier(tier);
    }
    // Mark entitlements as loaded so SecureShield tiles can render, and clear
    // any prior error (e.g. a retried/re-selected customer that now succeeds).
    setEntitlementsError(false);
    setEntitlementsLoaded(true);
  }, []);

  /* ── Handle internal client picker selection ── */
  const handleClientSelect = useCallback((client) => {
    // Cleared selection (X in the picker) — reset customer state so the
    // "select a customer" prompt returns, and drop the URL param.
    if (!client) {
      setCustomerName(null);
      setClientSlug(null);
      setEntitlementsLoaded(false);
      setEntitlementsError(false);
      entitlementsFetched.current = false;
      // Explicit clear — the frozen page-load snapshot must never
      // resurface, and any in-flight entitlements response is now stale.
      setSessionSelectionId(null);
      entitlementsSeq.current += 1;
      writeClientParam(null);
      return;
    }
    setCustomerName(client.customer_name);
    setSessionSelectionId(client.customer_id);
    entitlementsFetched.current = true;
    // Switching customers — reset entitlement state so the new selection shows
    // the loading state, not the previous customer's pills/tier or a stale error.
    setEntitlementsLoaded(false);
    setEntitlementsError(false);
    // Clear the previous customer's tier so a stale tier can't carry into the
    // new customer's showSecureShield / visibleServices before the new
    // entitlements resolve, and reset the selection to the default so the
    // reconciliation in applyEntitlements settles it for the new customer
    // (otherwise a carried-over slug like "academy" — valid for nearly every
    // customer — would keep showSecureShield false on a non-SS → SS switch).
    setSsTier(null);
    setSelectedSlug(getDefaultSlug(DEFAULT_ACTIVE_SLUGS));
    // Internal picker path: the chosen customer_id IS the blob slug. Set it
    // directly here — applyEntitlements no longer sets clientSlug (the
    // customer-user path gets it from /api/me instead).
    setClientSlug(client.customer_id);
    // Persist the selection in the URL so a refresh restores it.
    writeClientParam(client.customer_id);
    // Stale-response guard: only the LATEST selection's response may
    // touch the services bar. A slow response for a previously-viewed
    // customer resolves after a newer switch and is dropped here.
    const seq = ++entitlementsSeq.current;
    fetchEntitlements(client.tenant_id)
      .then((data) => {
        if (entitlementsSeq.current === seq) applyEntitlements(data);
      })
      .catch((e) => {
        if (entitlementsSeq.current !== seq) return;
        // Same honest-failure handling as the customer path: keep pills hidden
        // (entitlementsLoaded false) and flag the error so the main panel falls
        // through to PlaceholderDashboard rather than hanging on the spinner.
        // eslint-disable-next-line no-console
        console.warn("Entitlements unavailable for selected customer:", e);
        setEntitlementsError(true);
      });
  }, [applyEntitlements, writeClientParam]);

  /* ── Resolve identity (slug) then entitlements when user is available ──
     /api/me is the authoritative source for the customer slug: server-side
     rules (e.g. the Demo.Only pin) can rewrite the effective tenant after
     auth, so we must NOT infer the slug from the raw token tid. We fetch /me
     first, set clientSlug from it, then fetch entitlements using the RESOLVED
     tenant_id (me.tenant_id) for the services/tier bar. Entitlements failure
     never affects clientSlug — the dashboard can load from the resolved slug
     even if the services bar is unavailable. */
  useEffect(() => {
    if (entitlementsFetched.current) return;
    // Internal users get their slug + entitlements via ClientPicker selection
    if (isInternal) return;

    if (user?.tenantId) {
      entitlementsFetched.current = true;
      fetchMe()
        .then((me) => {
          if (me?.slug) setClientSlug(me.slug);
          if (me?.customer_name) setCustomerName(me.customer_name);
          // Entitlements drives only the services/tier bar; use the resolved
          // tenant, and never let its failure clobber the slug from /me.
          const entTenant = me?.tenant_id || user.tenantId;
          return fetchEntitlements(entTenant)
            .then(applyEntitlements)
            .catch((e) => {
              // eslint-disable-next-line no-console
              console.warn("Entitlements unavailable; dashboard still loads from /me slug:", e);
              // Do NOT set entitlementsLoaded here. Leaving it false keeps the
              // service pills hidden (customerLoaded false) — an honest empty bar
              // rather than confident-but-wrong "Inactive" badges for a customer
              // whose true status is unknown. The explicit error flag lets the
              // main panel fall through to PlaceholderDashboard instead of
              // hanging on the loading spinner.
              setEntitlementsError(true);
            });
        })
        .catch((e) => {
          // /me failed — without a resolved slug we cannot load the dashboard.
          // Surface it; do NOT fall back to a guessed/mock slug.
          // eslint-disable-next-line no-console
          console.error("Failed to resolve identity from /api/me:", e);
          entitlementsFetched.current = false; // allow a retry on next render
        });
    } else if (!isMsalConfigured) {
      // Local dev with no auth — mock entitlements only (no real identity).
      entitlementsFetched.current = true;
      fetchEntitlements("local-dev").then(applyEntitlements);
    }
  }, [user, isInternal, applyEntitlements]);

  const handleSelect = useCallback((slug) => {
    if (slug === "academy") {
      window.open("https://premium.patriotconsulting.com", "_blank", "noopener,noreferrer");
      return;
    }
    // Only the customer's OWN active SecureShield tier navigates. Upgrade tiers
    // (higher SS tiers they don't have) and non-SS services (mxdr/csp/adv-deploy)
    // are inert — hover delivers their info. ssTier null (non-SS customer) →
    // activeTierSlug null → nothing navigates (all pills inert or external).
    const activeTierSlug = ssTier ? TIER_TO_SLUG[ssTier] : null;
    if (slug !== activeTierSlug) return;
    setSelectedSlug(slug);
  }, [ssTier]);

  // Top-right static identity slot shows the LOGGED-IN USER (name → email →
  // neutral fallback). The company name reaches the dashboard via `customerName`
  // (passed to SecureShield365 below), so it is not lost by this slot showing the user.
  const userLabel = user?.name || user?.email || "Account";
  const userInitials =
    (user?.name
      ? user.name.split(" ").map(w => w[0]).join("").substring(0, 2)
      : (user?.email || "").charAt(0)
    ).toUpperCase() || "·";

  /* ── Resolve the customer's active SS slug from tier ──
     The API returns "secureshield" for any Secure Audit 365 contract.
     Map it to the tier-specific slug using secureshield_tier (TIER_TO_SLUG is
     defined at module scope, alongside SLUG_TO_TIER). */
  const resolvedSlugs = useMemo(() => {
    if (!activeSlugs.includes("secureshield")) return activeSlugs;
    const tierSlug = ssTier ? TIER_TO_SLUG[ssTier] : "secureshield";
    if (!tierSlug || tierSlug === "secureshield") return activeSlugs;
    // Replace "secureshield" with the tier-specific slug
    return activeSlugs.map(s => s === "secureshield" ? tierSlug : s);
  }, [activeSlugs, ssTier]);

  /* ── Tile assembly + filtering ──
     SS365 tier tiles, customer HAS a tier: render the current tier (Active) plus
     ALL higher tiers (each Upgrade); lower tiers are hidden (ssTag → null).
     SS365 tier tiles, customer has NO tier (non-SS365 customer): collapse to a
     single Inactive "SecureShield365" upsell pill (the flagship "secureshield"
     tile relabeled); silver/platinum are dropped.
     Non-SS365 service tiles (mxdr, csp, adv-deploy): badge is driven purely by
     entitlement active state — Active/green when active, Inactive/muted when
     not. Academy is always Active. */
  const visibleServices = (() => {
    return SERVICES.map((s) => {
      const isActive = resolvedSlugs.includes(s.slug);
      if (s.tier) {
        if (!entitlementsLoaded) return null; // avoid flashing before tier known
        // Non-SS365 customer (no tier): one Inactive umbrella pill only.
        if (!ssTier) {
          if (s.slug !== "secureshield") return null;
          return {
            ...s,
            shortTitle: "SecureShield365",
            title: "SecureShield365",
            tag: "Inactive",
            tagType: "muted",
            // Product-level umbrella copy — NOT the Gold tier-specific copy.
            description:
              "Ongoing Microsoft 365 security posture management — continuous assessment, remediation tracking, and compliance reporting, with tiered service levels.",
            features: ["Security posture scoring", "Remediation tracking", "Compliance reporting"],
            // learnMoreUrl + inquiryService carry through unchanged via ...s.
          };
        }
        // Customer HAS a tier: dynamic tag from ssTag(); null → hide this tile.
        const result = ssTag(s.slug, ssTier);
        if (!result) return null;
        return { ...s, tag: result.tag, tagType: result.tagType };
      }
      // Academy: always active regardless of entitlement
      if (s.slug === "academy") {
        return { ...s, tag: "Active", tagType: "green" };
      }
      // Other non-SS services (mxdr, csp, adv-deploy): Active or Inactive.
      return isActive
        ? { ...s, tag: "Active", tagType: "green" }
        : { ...s, tag: "Inactive", tagType: "muted" };
    }).filter(Boolean);
  })();

  const selectedService = visibleServices.find(s => s.slug === selectedSlug) || visibleServices[0];
  const isSelectedActive = resolvedSlugs.includes(selectedSlug);
  // Defense in depth: the SecureShield dashboard only mounts when the selected
  // tile is an SS slug AND the customer actually holds a SecureShield
  // entitlement (ssTier truthy). This guarantees it can never render for a
  // non-SS customer regardless of how selectedSlug was set.
  const showSecureShield = isSSSlug(selectedSlug) && !!ssTier;
  // Mirror the child page only while SecureShield365 is actually rendered.
  // Loading and no-entitlement placeholders must not inherit a stale Admin page.
  const showNoCustomerSecureShield = isInternal && !customerName && !clientSlug;
  const showSecureShieldChild = showNoCustomerSecureShield || showSecureShield;
  const onSecureShieldPageChange = useCallback((page) => {
    setSecureShieldPage(page);
  }, []);
  const onAdminPage = showSecureShieldChild && secureShieldPage === "admin";

  // Per-customer service pills (Active/Inactive/Upgrade) are meaningless without
  // a customer in context. Only render them once a customer is actually resolved
  // (clientSlug set — from /api/me for customers, or the picker for internal
  // users) AND that customer's entitlements have loaded. Until then the pill row
  // is empty; the rest of the header chrome stays visible.
  const customerLoaded = entitlementsLoaded && !!clientSlug;

  return (
    <div style={{
      minHeight: "100vh",
      fontFamily: "'DM Sans',system-ui,sans-serif",
      color: T.text,
    }}>
      {/* Inject popover animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes ssp-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── Header bar ── */}
      <header style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        height: 52,
        background: "rgba(7,20,38,0.92)",
        borderBottom: `1px solid ${T.border}`,
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 900,
        gap: 16,
        overflow: "visible",
      }}>
        {/* Left — branding */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          flexShrink: 0,
        }}>
          <img
            src="/patriot-logo.png"
            alt="Patriot Consulting"
            style={{ height: 36, width: "auto" }}
          />
        </div>

        {/* Center — service tile bar. The explicit minWidth is the sum of the
            tiles' floors + gaps, computed from the tile count (and the
            compact-mode floor) so it scales if services are added. It must be
            exactly this:
            - min-width:auto uses the LABELS' intrinsic text width as the
              floor (~956px for 7 tiles) — tiles never shrink and the whole
              document overflows on laptops (the original bug).
            - min-width:0 lets the nav collapse below the tiles' floors —
              tiles slide UNDER the picker (measured overlap at ≤1280px).
            Shrink order as space tightens: tiles to their floor first, then
            the picker 260→170; only below that does the header overflow. */}
        <nav style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flex: 1,
          minWidth: customerLoaded && visibleServices.length > 0
            ? visibleServices.length * (compactHeader ? PILL_MIN_COMPACT : PILL_MIN_FULL)
              + (visibleServices.length - 1) * 6
            : 0,
          justifyContent: "center",
          padding: "4px 0",
        }}>
          {customerLoaded && visibleServices.map(svc => (
            <ServiceTile
              key={svc.slug}
              service={svc}
              isActive={!(ssTier === "Bronze" && svc.tier) && (svc.slug === "academy" || resolvedSlugs.includes(svc.slug))}
              isSelected={!(ssTier === "Bronze" && svc.tier) && selectedSlug === svc.slug}
              onSelect={handleSelect}
              onRequestInfo={onRequestInfo}
              compact={compactHeader}
            />
          ))}
        </nav>

        {/* Right — user info + sign out. minWidth:0 (not min-width:auto) is
            required: the auto floor uses the name span's intrinsic text width
            (its 200px maxWidth), which blocks the zone from compressing and
            horizontally overflows the document at 1024px. With 0 the zone's
            CHILDREN provide the real floors (picker 170, name collapses to
            an ellipsis sliver, buttons keep content). The nav's computed
            minWidth guarantees tiles never slide under this zone. */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}>
          {/* Delivery-mode toggle — internal users only. Discreet; turns amber when ON. */}
          {isInternal && (deliveryMode || (!!clientSlug && !onAdminPage)) && (
            <button
              onClick={toggleDeliveryMode}
              title={deliveryMode
                ? "Presentation Mode is ON — internal controls hidden. Click to exit (confirms)."
                : "Hide internal controls to safely screen-share with a client."}
              style={{
                fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 6,
                cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                border: `1px solid ${deliveryMode ? "rgba(245,200,80,0.55)" : "rgba(255,255,255,0.14)"}`,
                background: deliveryMode ? "rgba(245,200,80,0.15)" : "rgba(255,255,255,0.04)",
                color: deliveryMode ? "#f5d98a" : T.muted,
              }}
            >{deliveryMode ? "● Exit Presentation Mode" : "Presentation Mode"}</button>
          )}
          {isInternal && !deliveryMode ? (
            // Restore source is LIVE session state; the frozen page-load
            // snapshot applies only while the session is untouched
            // (undefined). After a clear (null) nothing restores.
            <ClientPicker
              onSelect={handleClientSelect}
              initialClientId={sessionSelectionId === undefined ? initialClientId : sessionSelectionId}
            />
          ) : (
            <>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "rgba(68,166,255,0.15)",
                border: "1px solid rgba(68,166,255,0.3)",
                display: "grid", placeItems: "center",
                fontSize: 11, fontWeight: 700, color: T.blue,
              }}>{userInitials}</div>
              {/* min(200px, 10vw): at laptop widths the signed-in user's own
                  name yields space so the service pills keep their FULL names
                  (the pill name is information; the user knows who they are).
                  At 1024px this is what lets all 7 dot-mode pills render
                  untruncated for customer users. */}
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text, maxWidth: "min(200px, 10vw)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userLabel}</span>
            </>
          )}
          {isMsalConfigured && <SignOutButton />}
        </div>
      </header>

      {/* ── Dashboard content ── */}
      {showNoCustomerSecureShield ? (
        // Internal user who hasn't picked a customer yet — mount the dashboard
        // shell slug-less so Admin stays reachable; Azure SecureAudit and
        // Presentation Mode require a selected customer. Its main panel shows
        // the select-a-customer prompt and
        // customer-scoped pages render disabled. Gated on customerName (set
        // synchronously in handleClientSelect) so this state does NOT flash
        // back during the brief window after a selection while
        // entitlements/clientSlug are still resolving — the dashboard's own
        // loading state covers that gap.
        <SecureShield365 slug={null} customerName={null} deliveryMode={deliveryMode} onPageChange={onSecureShieldPageChange} />
      ) : clientSlug && !customerLoaded && !entitlementsError ? (
        // Customer is resolved (clientSlug set from /api/me or the picker) and
        // their entitlements are still IN FLIGHT (not yet loaded, no error).
        // Show a neutral loading state rather than flashing PlaceholderDashboard
        // (Gold marketing card) before ssTier arrives.
        // This window is bounded by !entitlementsError: a successful fetch sets
        // entitlementsLoaded; a failed fetch sets entitlementsError. Either way
        // this condition goes false, so it cannot hang. On entitlements FAILURE
        // we fall through to PlaceholderDashboard with pills hidden (customerLoaded
        // false), not an infinite spinner. The /api/me-failure path leaves
        // clientSlug null, so it skips this branch entirely (unchanged).
        <DashboardLoading />
      ) : showSecureShield ? (
        <SecureShield365 slug={clientSlug} customerName={customerName} deliveryMode={deliveryMode} onPageChange={onSecureShieldPageChange} />
      ) : (
        <PlaceholderDashboard
          service={selectedService}
          isActive={isSelectedActive}
        />
      )}

      {/* Service inquiry modal — single instance, portal'd to document.body */}
      <ServiceInquiryModal
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        currentUser={{ name: user?.name, email: user?.email, company: customerName }}
        service={inquiryService}
      />
      {showWarning && (
        <IdleWarningModal
          remainingMs={remainingMs}
          onStay={staySignedIn}
          onSignOut={signOutNow}
        />
      )}
    </div>
  );
}
